import { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Stack,
  Group,
  Button,
  Text,
  Select,
  Divider,
  NumberInput,
  Badge,
  ActionIcon,
  Box,
  Checkbox,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { RotateCcw } from 'react-feather';
import { cohortCompensationApi } from '../../../../services/api/cohortCompensation';
import type {
  AudienceType,
  RateCardRates,
  CohortCompensationConfig,
  CompensationCalculationInputs,
  OneOnOneSdaType,
} from '../../../../types';
import { ONE_ON_ONE_SDA_LABELS } from '../../../../types';

interface ConfigureCompensationModalProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  config: CohortCompensationConfig | null;
  resolvedRates: RateCardRates | null;
  cohortModalities: string[];
  sessionDuration: number | null;
  /**
   * MED-731: 1:1 session types actually configured on the cohort. Each row
   * in the 1:1 SDA section is gated on this list — unconfigured session
   * types (e.g. 2:1, 360_debrief) are not rendered.
   */
  configuredOneOnOneSessionTypes: OneOnOneSdaType[];
  /**
   * MED-731: 1:1 session durations (minutes) configured on the cohort.
   * Drives which duration columns are rendered per session type.
   */
  configuredOneOnOneDurations: number[];
  /** MED-731: True when the cohort has at least one in_person_booster event. */
  hasInPersonEvents: boolean;
  /** MED-910 AC0m–AC0q: read-only cohort-config values for the modal. */
  calculationInputs: CompensationCalculationInputs | null;
  onSaved: () => void;
}

const DEFAULT_HUDDLE_COUNT = 2;

/** AC0m–AC0q: render the read-only "Calculation Inputs" list. A null field
 * shows the documented "Not set yet — defaults to [X]" indicator. */
function CalculationInputsSection({
  inputs,
  modalities,
}: {
  inputs: CompensationCalculationInputs;
  modalities: string[];
}) {
  const lines: string[] = [];
  if (modalities.includes('group_coaching')) {
    const n = inputs.group_coaching_session_count;
    const d = inputs.group_coaching_duration_minutes;
    lines.push(
      n != null && d != null
        ? `${n} group coaching session${n === 1 ? '' : 's'} × ${d} min`
        : 'Group coaching sessions: Not set yet',
    );
  }
  if (modalities.includes('workshop')) {
    const w = inputs.workshop_count;
    lines.push(w != null ? `${w} workshop${w === 1 ? '' : 's'}` : 'Workshops: Not set yet');
  }
  if (modalities.includes('one_on_one')) {
    const n = inputs.one_on_one_session_count;
    const d = inputs.one_on_one_duration_minutes;
    lines.push(
      n != null && d != null
        ? `1:1 packet: ${n} session${n === 1 ? '' : 's'} @ ${d} min`
        : '1:1 packet: Not set yet',
    );
  }
  // MED-1156: the backend always supplies the Medley-standard comp target
  // group size (7); the matching-server value (5) is never used here. The
  // null branch is defensive only — its copy must still read 7, not a stale 6.
  lines.push(
    inputs.target_group_size != null
      ? `Target group size: ${inputs.target_group_size} participants`
      : 'Target group size: Not set yet — defaults to 7 participants',
  );
  lines.push(
    inputs.program_months != null
      ? `Program length: ${inputs.program_months} month${inputs.program_months === 1 ? '' : 's'}`
      : 'Program length: Not set yet — defaults to 4 months',
  );

  return (
    <Stack gap="xs">
      <Text size="xs" fw={600} tt="uppercase" c="dimmed">
        Calculation Inputs
      </Text>
      <Stack gap={2} style={{ background: '#F8F6F1', borderRadius: 8, padding: '12px 16px' }}>
        {lines.map(line => (
          <Text key={line} size="sm" c={line.includes('Not set yet') ? 'dimmed' : undefined}>
            • {line}
          </Text>
        ))}
        <Text size="xs" c="dimmed" fs="italic" mt={6}>
          To change these, edit the cohort configuration.
        </Text>
      </Stack>
    </Stack>
  );
}

const AUDIENCE_OPTIONS = [
  { value: 'emerging', label: 'Emerging Leaders' },
  { value: 'established', label: 'Established Leaders' },
  { value: 'executive', label: 'Executive Leaders' },
];

/**
 * MED-910: canonical exclusion-component keys (mirror of backend
 * EXCLUSION_COMPONENT_KEYS in medley-api/constants/compensation.ts). One
 * Include/exclude toggle per key; `group_session` SDA is never excludable
 * (flat-rate monthly, AC1).
 */
type ExclusionComponentKey =
  | 'huddle'
  | 'immersion'
  | 'orientation'
  | 'wrap_up'
  | 'one_on_one'
  | 'workshop'
  | 'in_person'
  | 'coach_debrief';

interface RateRow {
  key: string;
  label: string;
  /** 'group_session' | 'one_on_one_sda' | 'flat' */
  category: string;
  audienceType?: string;
  duration?: string;
  /** MED-693: required when category === 'one_on_one_sda' */
  sessionType?: OneOnOneSdaType;
  flatKey?: string;
  unit?: string;
  /**
   * MED-910: the exclusion component this row's Include toggle controls.
   * null ⇒ not excludable (Group Session SDA — flat-rate monthly, AC1).
   */
  componentKey?: ExclusionComponentKey | null;
  /** MED-910 AC0j: true only for the Huddle row (editable Count input). */
  hasCount?: boolean;
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toLocaleString()}`;
}

/**
 * MED-731: Build the rate rows shown in the Configure Compensation modal.
 * Every row is gated on the cohort's actual modality configuration — nothing
 * is rendered unconditionally. The footer "Only rates relevant to this
 * cohort's modalities are shown" now reflects reality.
 *
 * Gating rules:
 *   - Group Session SDA row → cohort has group_coaching modality + a session
 *     duration. Single row for the configured duration only.
 *   - 1:1 SDA rows → one row per (configured session type × configured
 *     duration) pair. Never every permutation.
 *   - Huddle / Immersion / Cohort Events → tied to group_coaching.
 *   - Workshop Facilitation → cohort has workshop modality.
 *   - In-Person Half/Full Day → cohort has at least one in_person_booster
 *     event scheduled.
 *   - Coach Debrief flat rate → cohort has at least one 1:1 session
 *     configured as 360_debrief (matches the compensation engine's gate on
 *     the $100 flat fee — see CompensationCalculationService).
 */
function buildRateRows(
  modalities: string[],
  sessionDuration: number | null,
  configuredOneOnOneSessionTypes: OneOnOneSdaType[],
  configuredOneOnOneDurations: number[],
  hasInPersonEvents: boolean,
): RateRow[] {
  const rows: RateRow[] = [];
  const hasGroupCoaching = modalities.includes('group_coaching');
  const hasOneOnOne = modalities.includes('one_on_one');
  const hasWorkshop = modalities.includes('workshop');

  if (hasGroupCoaching && sessionDuration) {
    // AC1: Group Session SDA is flat-rate monthly — NO Include toggle.
    rows.push({
      key: `group_session_${sessionDuration}`,
      label: `Group Session SDA (${sessionDuration} min)`,
      category: 'group_session',
      duration: String(sessionDuration),
      componentKey: null,
    });
  }

  if (hasOneOnOne && configuredOneOnOneSessionTypes.length > 0) {
    const durationsAscending = [...configuredOneOnOneDurations].sort((a, b) => a - b);
    for (const sessionType of configuredOneOnOneSessionTypes) {
      for (const duration of durationsAscending) {
        rows.push({
          key: `one_on_one_sda_${sessionType}_${duration}`,
          label: `${ONE_ON_ONE_SDA_LABELS[sessionType]} (${duration} min)`,
          category: 'one_on_one_sda',
          sessionType,
          duration: String(duration),
          componentKey: 'one_on_one',
        });
      }
    }
  }

  if (hasGroupCoaching) {
    rows.push(
      {
        key: 'flat_huddle',
        label: 'Huddle',
        category: 'flat',
        flatKey: 'huddle',
        componentKey: 'huddle',
        hasCount: true, // MED-910 AC0j: only the Huddle row has a Count input
      },
      {
        key: 'flat_immersion',
        label: 'Immersion',
        category: 'flat',
        flatKey: 'immersion',
        componentKey: 'immersion',
      },
      // MED-910 AC0a: "Cohort Events" is split into two independent rows.
      {
        key: 'flat_orientation',
        label: 'Orientation',
        category: 'flat',
        flatKey: 'orientation',
        unit: '/hr',
        componentKey: 'orientation',
      },
      {
        key: 'flat_wrap_up',
        label: 'Wrap-Up',
        category: 'flat',
        flatKey: 'wrap_up',
        unit: '/hr',
        componentKey: 'wrap_up',
      },
    );
  }

  if (hasWorkshop) {
    rows.push({
      key: 'flat_workshop',
      label: 'Workshop Facilitation',
      category: 'flat',
      flatKey: 'workshop',
      componentKey: 'workshop',
    });
  }

  if (hasInPersonEvents) {
    rows.push(
      {
        key: 'flat_in_person_half',
        label: 'In-Person Half Day',
        category: 'flat',
        flatKey: 'in_person_half_day',
        componentKey: 'in_person',
      },
      {
        key: 'flat_in_person_full',
        label: 'In-Person Full Day',
        category: 'flat',
        flatKey: 'in_person_full_day',
        componentKey: 'in_person',
      },
    );
  }

  if (configuredOneOnOneSessionTypes.includes('360_debrief')) {
    rows.push({
      key: 'flat_coach_debrief',
      label: 'Coach Debrief',
      category: 'flat',
      flatKey: 'coach_debrief',
      componentKey: 'coach_debrief',
    });
  }

  return rows;
}

function getResolvedValue(
  rates: RateCardRates,
  row: RateRow,
  audienceType: AudienceType,
): number {
  if (row.flatKey) {
    return (rates.flat as Record<string, number>)[row.flatKey] ?? 0;
  }
  if (row.category === 'one_on_one_sda' && row.sessionType) {
    return rates.one_on_one_sda?.[row.sessionType]?.[audienceType]?.[row.duration!] ?? 0;
  }
  return (
    (rates.group_session as Record<string, Record<string, number>>)?.[audienceType]?.[row.duration!] ?? 0
  );
}

function getOverrideValue(
  overrides: Partial<RateCardRates>,
  row: RateRow,
  audienceType: AudienceType,
): number | undefined {
  if (row.flatKey) {
    return (overrides.flat as Record<string, number> | undefined)?.[row.flatKey];
  }
  if (row.category === 'one_on_one_sda' && row.sessionType) {
    return overrides.one_on_one_sda?.[row.sessionType]?.[audienceType]?.[row.duration!];
  }
  return overrides.group_session?.[audienceType]?.[row.duration!];
}

function setOverrideValue(
  overrides: Partial<RateCardRates>,
  row: RateRow,
  audienceType: AudienceType,
  value: number,
): Partial<RateCardRates> {
  const result = JSON.parse(JSON.stringify(overrides)) as Partial<RateCardRates>;
  if (row.flatKey) {
    if (!result.flat) result.flat = {} as RateCardRates['flat'];
    (result.flat as Record<string, number>)[row.flatKey] = value;
    return result;
  }
  if (row.category === 'one_on_one_sda' && row.sessionType) {
    if (!result.one_on_one_sda) result.one_on_one_sda = {} as RateCardRates['one_on_one_sda'];
    const sda = result.one_on_one_sda as Record<string, Record<string, Record<string, number>>>;
    if (!sda[row.sessionType]) sda[row.sessionType] = {};
    if (!sda[row.sessionType][audienceType]) sda[row.sessionType][audienceType] = {};
    sda[row.sessionType][audienceType][row.duration!] = value;
    return result;
  }
  if (!result.group_session) result.group_session = {} as RateCardRates['group_session'];
  const gs = result.group_session as Record<string, Record<string, number>>;
  if (!gs[audienceType]) gs[audienceType] = {};
  gs[audienceType][row.duration!] = value;
  return result;
}

function removeOverrideValue(
  overrides: Partial<RateCardRates>,
  row: RateRow,
  audienceType: AudienceType,
): Partial<RateCardRates> {
  const result = JSON.parse(JSON.stringify(overrides)) as Partial<RateCardRates>;
  if (row.flatKey && result.flat) {
    delete (result.flat as Record<string, number>)[row.flatKey];
    if (Object.keys(result.flat).length === 0) delete result.flat;
    return result;
  }
  if (row.category === 'one_on_one_sda' && row.sessionType && result.one_on_one_sda) {
    const sda = result.one_on_one_sda as Record<string, Record<string, Record<string, number>>>;
    const tier = sda[row.sessionType]?.[audienceType];
    if (tier) {
      delete tier[row.duration!];
      if (Object.keys(tier).length === 0) delete sda[row.sessionType][audienceType];
      if (sda[row.sessionType] && Object.keys(sda[row.sessionType]).length === 0) {
        delete sda[row.sessionType];
      }
    }
    if (Object.keys(sda).length === 0) delete result.one_on_one_sda;
    return result;
  }
  if (result.group_session) {
    const gs = result.group_session as Record<string, Record<string, number>>;
    if (gs[audienceType]) {
      delete gs[audienceType][row.duration!];
      if (Object.keys(gs[audienceType]).length === 0) delete gs[audienceType];
    }
    if (Object.keys(gs).length === 0) delete result.group_session;
  }
  return result;
}

export function ConfigureCompensationModal({
  opened,
  onClose,
  cohortId,
  config,
  resolvedRates,
  cohortModalities,
  sessionDuration,
  configuredOneOnOneSessionTypes,
  configuredOneOnOneDurations,
  hasInPersonEvents,
  calculationInputs,
  onSaved,
}: ConfigureCompensationModalProps) {
  const [audienceType, setAudienceType] = useState<AudienceType | null>(
    config?.audience_type ?? null,
  );
  const [overrides, setOverrides] = useState<Partial<RateCardRates>>(
    config?.rate_overrides ?? {},
  );
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<number>(0);
  const [saving, setSaving] = useState(false);
  // MED-910: cohort-level exclusions + configurable huddle count.
  const [excludedComponents, setExcludedComponents] = useState<string[]>(
    config?.excluded_components ?? [],
  );
  const [huddleCount, setHuddleCount] = useState<number>(
    config?.huddle_count ?? DEFAULT_HUDDLE_COUNT,
  );

  // Reset state when modal opens
  useEffect(() => {
    if (opened) {
      setAudienceType(config?.audience_type ?? null);
      setOverrides(config?.rate_overrides ?? {});
      setEditingKey(null);
      setExcludedComponents(config?.excluded_components ?? []);
      setHuddleCount(config?.huddle_count ?? DEFAULT_HUDDLE_COUNT);
    }
  }, [opened, config]);

  // AC2: a component is included unless explicitly in the excluded set.
  const isComponentExcluded = (key: ExclusionComponentKey | null | undefined): boolean =>
    key != null && excludedComponents.includes(key);

  const toggleComponent = (key: ExclusionComponentKey) => {
    setExcludedComponents(prev =>
      prev.includes(key) ? prev.filter(c => c !== key) : [...prev, key],
    );
  };

  const rateRows = useMemo(
    () =>
      buildRateRows(
        cohortModalities,
        sessionDuration,
        configuredOneOnOneSessionTypes,
        configuredOneOnOneDurations,
        hasInPersonEvents,
      ),
    [
      cohortModalities,
      sessionDuration,
      configuredOneOnOneSessionTypes,
      configuredOneOnOneDurations,
      hasInPersonEvents,
    ],
  );

  const handleSave = async () => {
    if (!audienceType) {
      notifications.show({ title: 'Error', message: 'Please select an audience type', color: 'red' });
      return;
    }
    try {
      setSaving(true);
      await cohortCompensationApi.configure(
        cohortId,
        audienceType,
        overrides,
        excludedComponents,
        huddleCount,
      );
      onSaved();
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to save configuration', color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  const handleStartEdit = (row: RateRow) => {
    if (!audienceType || !resolvedRates) return;
    setEditingKey(row.key);
    const currentValue = getOverrideValue(overrides, row, audienceType);
    const resolvedValue = getResolvedValue(resolvedRates, row, audienceType);
    setEditValue((currentValue ?? resolvedValue) / 100);
  };

  const handleConfirmEdit = (row: RateRow) => {
    if (!audienceType) return;
    setOverrides((prev) => setOverrideValue(prev, row, audienceType, editValue * 100));
    setEditingKey(null);
  };

  const handleResetOverride = (row: RateRow) => {
    if (!audienceType) return;
    setOverrides((prev) => removeOverrideValue(prev, row, audienceType));
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={700} size="lg">
          Configure Compensation
        </Text>
      }
      size="lg"
    >
      <Stack gap="md">
        {/* Audience Type */}
        <Stack gap="xs">
          <Text size="xs" fw={600} tt="uppercase" c="dimmed">
            Audience Type
          </Text>
          <Select
            data={AUDIENCE_OPTIONS}
            value={audienceType}
            onChange={(val) => setAudienceType(val as AudienceType)}
            placeholder="Select audience type"
          />
        </Stack>

        <Divider />

        {/* MED-910 AC0m–AC0q: Calculation Inputs (read-only) */}
        {calculationInputs && (
          <>
            <CalculationInputsSection
              inputs={calculationInputs}
              modalities={cohortModalities}
            />
            <Divider />
          </>
        )}

        {/* Rate Overrides */}
        <Stack gap="xs">
          <Text size="xs" fw={600} tt="uppercase" c="dimmed">
            Rate Overrides
          </Text>
          <Text size="sm" c="dimmed">
            Override rates for this cohort. Changes only affect this cohort&apos;s compensation
            calculations.
          </Text>
        </Stack>

        {audienceType && resolvedRates ? (
          <Stack
            gap={0}
            style={{ border: '1px solid #E8E6E1', borderRadius: 8, overflow: 'hidden' }}
          >
            {rateRows.map((row) => {
              const overrideVal = getOverrideValue(overrides, row, audienceType);
              const isOverridden = overrideVal !== undefined;
              const displayValue = isOverridden
                ? overrideVal
                : getResolvedValue(resolvedRates, row, audienceType);
              const isEditing = editingKey === row.key;
              const excludable = row.componentKey != null;
              const excluded = isComponentExcluded(row.componentKey);

              return (
                <Group
                  key={row.key}
                  justify="space-between"
                  px="md"
                  py="sm"
                  wrap="nowrap"
                  style={{
                    borderBottom: '1px solid #F0EEE9',
                    background: isOverridden && !excluded ? '#FDF8F0' : undefined,
                    opacity: excluded ? 0.55 : 1,
                  }}
                >
                  <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                    {/* AC1: Include toggle for every attendance-dependent row.
                        Group Session SDA (componentKey === null) shows "—". */}
                    {excludable ? (
                      <Checkbox
                        size="sm"
                        checked={!excluded}
                        onChange={() => toggleComponent(row.componentKey as ExclusionComponentKey)}
                        aria-label={`Include ${row.label} in compensation`}
                      />
                    ) : (
                      <Tooltip label="Always included (flat-rate monthly)">
                        <Text size="sm" c="dimmed" w={20} ta="center">
                          —
                        </Text>
                      </Tooltip>
                    )}
                    <Stack gap={0} style={{ minWidth: 0 }}>
                      <Group gap="xs" wrap="nowrap">
                        <Text size="sm" fw={500} td={excluded ? 'line-through' : undefined}>
                          {row.label}
                        </Text>
                        {isOverridden && !excluded && (
                          <Badge size="xs" variant="light" color="orange">
                            OVERRIDE
                          </Badge>
                        )}
                      </Group>
                      {excluded && (
                        <Text size="xs" c="dimmed">
                          Excluded from this cohort&apos;s compensation
                        </Text>
                      )}
                    </Stack>
                  </Group>

                  {/* MED-910 AC0j: Count column — editable for Huddle only. */}
                  {row.hasCount ? (
                    <NumberInput
                      value={huddleCount}
                      onChange={(val) => setHuddleCount(Math.max(0, Number(val) || 0))}
                      min={0}
                      step={1}
                      decimalScale={0}
                      size="xs"
                      w={64}
                      disabled={excluded}
                      aria-label="Expected huddle count"
                    />
                  ) : (
                    <Text size="sm" c="dimmed" w={64} ta="center">
                      —
                    </Text>
                  )}

                  <Group gap="xs" wrap="nowrap">
                    {isEditing ? (
                      <>
                        <Text size="sm" c="dimmed">
                          $
                        </Text>
                        <NumberInput
                          value={editValue}
                          onChange={(val) => setEditValue(Number(val) || 0)}
                          min={0}
                          decimalScale={0}
                          thousandSeparator=","
                          size="xs"
                          w={100}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleConfirmEdit(row);
                            if (e.key === 'Escape') setEditingKey(null);
                          }}
                        />
                        <Button size="xs" variant="subtle" onClick={() => handleConfirmEdit(row)}>
                          Save
                        </Button>
                      </>
                    ) : (
                      <>
                        <Text
                          size="sm"
                          fw={isOverridden ? 600 : 400}
                          c={isOverridden ? 'orange' : undefined}
                        >
                          {formatCents(displayValue)}{row.unit || ''}
                        </Text>
                        <Button
                          size="xs"
                          variant="subtle"
                          color="gray"
                          onClick={() => handleStartEdit(row)}
                        >
                          Edit
                        </Button>
                        {isOverridden && (
                          <ActionIcon
                            size="sm"
                            variant="subtle"
                            color="gray"
                            onClick={() => handleResetOverride(row)}
                            title="Reset to default"
                          >
                            <RotateCcw size={14} />
                          </ActionIcon>
                        )}
                      </>
                    )}
                  </Group>
                </Group>
              );
            })}
          </Stack>
        ) : (
          <Box py="md">
            <Text size="sm" c="dimmed" ta="center">
              Select an audience type to see applicable rates.
            </Text>
          </Box>
        )}

        <Text size="xs" c="dimmed" fs="italic">
          Only rates relevant to this cohort&apos;s modalities are shown. Defaults from{' '}
          <Text component="span" size="xs" fw={600}>
            Compensation Settings
          </Text>
          .
        </Text>

        {/* Footer */}
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving} disabled={!audienceType}>
            Save Configuration
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
